import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ModalActionButtons from '../modal/ModalActionButtons';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  SpreadsheetColumnType,
  SpreadsheetColumnTypeMap
} from '../../constants/spreadsheetColumnTypes';
import { useSnackbar } from '../../context/SnackbarContext';
import { isValidColumnName } from '../../utils/columnNameValidation';
import ColumnMappingTable from './ColumnMappingComponent/ColumnMappingTable';

interface ColumnMappingComponentProps {
  existingColumns: GridColDef[];
  /** Optional map of field name -> col_type for existing columns (e.g. from getColumns) */
  existingColumnTypes?: Record<string, string>;
  detectedColumns: string[];
  protectedFields: string[];
  /** Optional title for the mapping section. Default: "Map import columns to spreadsheet columns" */
  mappingTitle?: string;
  /** Optional initial mapping (e.g. when editing a scheduled import). Keys = target/campaign columns, values = source/detected column. */
  initialMapping?: Record<string, string | null>;
  /** Optional display labels for source columns (e.g. HubSpot field label for apiName). When not provided, values are shown as-is. */
  detectedColumnLabels?: Record<string, string>;
  onConfirm: (
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => void;
  onCancel: () => void;
  triggeredFromProspectPage?: boolean;
  hideTitle?: boolean;
  hideButtons?: boolean;
  onMappingChange?: (mapping: Record<string, string | null>) => void;
  /** Sync column types to parent (for modals that control confirm externally) */
  onColumnTypesChange?: (columnTypes: SpreadsheetColumnTypeMap) => void;
  /** Optional extra disable for primary button (e.g. when schedule config requires a match key) */
  primaryDisabled?: boolean;
  /** Tooltip when primary is disabled via primaryDisabled */
  primaryDisabledTooltip?: string;
  /** Optional "exactly one" primary selector shown per row (radio) */
  enablePrimarySelection?: boolean;
  /** The currently selected primary target field (campaign column) */
  primaryField?: string;
  /** Called when user selects a different primary field */
  onPrimaryFieldChange?: (field: string) => void;
}

const ColumnMappingComponent: React.FC<ColumnMappingComponentProps> = ({
  existingColumns,
  existingColumnTypes,
  detectedColumns,
  protectedFields,
  mappingTitle,
  initialMapping: initialMappingProp,
  detectedColumnLabels,
  onConfirm,
  onCancel,
  triggeredFromProspectPage = false,
  hideTitle = false,
  hideButtons = false,
  onMappingChange,
  onColumnTypesChange,
  primaryDisabled: primaryDisabledProp,
  primaryDisabledTooltip: primaryDisabledTooltipProp,
  enablePrimarySelection = false,
  primaryField,
  onPrimaryFieldChange
}) => {
  const { showSnackbar } = useSnackbar();
  const [columnMapping, setColumnMapping] = useState<
    Record<string, string | null>
  >({});
  const [targetFields, setTargetFields] = useState<string[]>([]);
  const [newColumnName, setNewColumnName] = useState<string>('');
  const [newColumnType, setNewColumnType] =
    useState<SpreadsheetColumnType>('str');
  const [customColumnTypes, setCustomColumnTypes] =
    useState<SpreadsheetColumnTypeMap>({});

  const prevFieldsRef = useRef<string[]>([]);

  useEffect(() => {
    const fields = existingColumns
      .filter((c) => !c.field.startsWith('_'))
      .map((c) => c.field);

    // Check if fields actually changed by comparing field names using Set
    const prevFieldsSet = new Set(prevFieldsRef.current);
    const fieldsSet = new Set(fields);
    const fieldsChanged =
      prevFieldsRef.current.length !== fields.length ||
      prevFieldsRef.current.some((field) => !fieldsSet.has(field)) ||
      fields.some((field) => !prevFieldsSet.has(field));

    setTargetFields(fields);
    prevFieldsRef.current = fields;

    // Only reset mapping if fields actually changed or mapping is empty
    setColumnMapping((prevMapping) => {
      // When parent provides initial mapping (e.g. editing a saved job), always apply it
      const hasInitialFromParent =
        initialMappingProp && Object.keys(initialMappingProp).length > 0;
      // If fields haven't changed and we have mappings and no initial from parent, preserve them
      if (
        !fieldsChanged &&
        Object.keys(prevMapping).length > 0 &&
        !hasInitialFromParent
      ) {
        // Just ensure all current fields exist in mapping
        const updatedMapping = { ...prevMapping };
        fields.forEach((field) => {
          if (updatedMapping[field] === undefined) {
            updatedMapping[field] = detectedColumns.includes(field)
              ? field
              : null;
          }
        });
        // Remove mappings for fields that no longer exist
        Object.keys(updatedMapping).forEach((field) => {
          if (!fields.includes(field)) {
            delete updatedMapping[field];
          }
        });
        return updatedMapping;
      }

      // Initialize new mapping (use initialMappingProp when provided and valid)
      const next: Record<string, string | null> = {};
      const detectedSet = new Set(detectedColumns);
      fields.forEach((field) => {
        const fromProp = initialMappingProp?.[field];
        if (fromProp != null && fromProp !== '' && detectedSet.has(fromProp)) {
          next[field] = fromProp;
        } else if (
          prevMapping[field] !== undefined &&
          detectedSet.has(prevMapping[field] || '')
        ) {
          next[field] = prevMapping[field];
        } else {
          next[field] = detectedSet.has(field) ? field : null;
        }
      });
      return next;
    });
  }, [existingColumns, detectedColumns, initialMappingProp]);

  // Sync mapping to parent when it changes (including initial load) so parent can e.g. show primary key options
  useEffect(() => {
    if (onMappingChange) {
      onMappingChange(columnMapping);
    }
  }, [columnMapping, onMappingChange]);

  const handleMappingChange = (field: string, value: string | null) => {
    const newMapping = { ...columnMapping, [field]: value };
    setColumnMapping(newMapping);
    if (onMappingChange) {
      onMappingChange(newMapping);
    }
  };

  // Derive column types: existing columns from existingColumnTypes or GridColDef.type, merge with customColumnTypes
  const columnTypes: SpreadsheetColumnTypeMap = React.useMemo(() => {
    const fromExisting: SpreadsheetColumnTypeMap = {};
    existingColumns
      .filter((c) => !c.field.startsWith('_'))
      .forEach((col) => {
        const existingType = existingColumnTypes?.[col.field];
        if (
          existingType &&
          ['str', 'url', 'num', 'bool', 'date'].includes(existingType)
        ) {
          fromExisting[col.field] = existingType as SpreadsheetColumnType;
        } else {
          fromExisting[col.field] = col.type === 'number' ? 'num' : 'str';
        }
      });
    return { ...fromExisting, ...customColumnTypes };
  }, [existingColumns, existingColumnTypes, customColumnTypes]);

  const handleColumnTypeChange = (
    field: string,
    type: SpreadsheetColumnType
  ) => {
    setCustomColumnTypes((prev) => ({ ...prev, [field]: type }));
  };

  useEffect(() => {
    if (onColumnTypesChange) {
      onColumnTypesChange(columnTypes);
    }
  }, [columnTypes, onColumnTypesChange]);

  useEffect(() => {
    setCustomColumnTypes({});
  }, [existingColumns]);

  const handleAddColumn = (nameOverride?: string) => {
    const name = (nameOverride ?? newColumnName).trim();
    if (!name) return;

    // Validate column name
    const validation = isValidColumnName(name);
    if (!validation.valid) {
      showSnackbar(validation.error || 'Invalid column name', 'error');
      return;
    }

    // Check for case-insensitive duplicates
    const lowerName = name.toLowerCase();
    if (targetFields.some((f) => f.toLowerCase() === lowerName)) {
      showSnackbar('Column name already exists', 'error');
      return;
    }

    setTargetFields((prev) => [...prev, name]);
    setColumnMapping((prev) => ({ ...prev, [name]: null }));
    setNewColumnName('');
    setCustomColumnTypes((prev) => ({
      ...prev,
      [name]: newColumnType
    }));
    setNewColumnType('str');
  };

  const handleRemoveField = (field: string) => {
    setTargetFields((prev) => prev.filter((f) => f !== field));
    setColumnMapping((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setCustomColumnTypes((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const allProtectedMapped = protectedFields.every(
    (field) => columnMapping[field]
  );

  const hasAtLeastOneSourceMapped = Object.values(columnMapping).some(
    (v) => v != null && v !== ''
  );
  const confirmDisabled =
    !hasAtLeastOneSourceMapped ||
    !allProtectedMapped ||
    (primaryDisabledProp ?? false);
  const primaryDisabledTooltip =
    primaryDisabledTooltipProp ??
    (confirmDisabled
      ? !hasAtLeastOneSourceMapped
        ? 'Map at least one CRM column to a spreadsheet column to continue.'
        : 'Map all required fields to continue.'
      : undefined);

  return (
    <Box>
      {!hideTitle && (
        <Typography variant="h6" gutterBottom>
          {mappingTitle ?? 'Map import columns to spreadsheet columns'}
        </Typography>
      )}

      <ColumnMappingTable
        targetFields={targetFields}
        columnMapping={columnMapping}
        columnTypes={columnTypes}
        detectedColumns={detectedColumns}
        detectedColumnLabels={detectedColumnLabels}
        protectedFields={protectedFields}
        triggeredFromProspectPage={triggeredFromProspectPage}
        newColumnName={newColumnName}
        newColumnType={newColumnType}
        onMappingChange={handleMappingChange}
        onColumnTypeChange={handleColumnTypeChange}
        onRemoveField={handleRemoveField}
        onNewColumnNameChange={setNewColumnName}
        onNewColumnTypeChange={setNewColumnType}
        onAddColumn={handleAddColumn}
        enablePrimarySelection={enablePrimarySelection}
        primaryField={primaryField}
        onPrimaryFieldChange={onPrimaryFieldChange}
      />

      {!hideButtons && (
        <ModalActionButtons
          asDialogActions={false}
          secondaryLabel="Back"
          primaryLabel="Confirm"
          onSecondary={onCancel}
          onPrimary={() => onConfirm(columnMapping, customColumnTypes)}
          primaryDisabled={confirmDisabled}
          primaryDisabledTooltip={primaryDisabledTooltip}
        />
      )}
    </Box>
  );
};

export default ColumnMappingComponent;
